import map from 'lodash/map'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import toFinite from 'lodash/toFinite'
import findIndex from 'lodash/findIndex'
import size from 'lodash/size'
import range from 'lodash/range'
import filter from 'lodash/filter'

import { annuity } from '@utils/financeCalculations'
import { getYearFromAge } from '@utils/dateParser'
import { buildDatasetConfig, CHART_NAMES } from './config'
import { ChartTypes } from '../index'

// Construct the `datasets` object of the projections
// in the shape required by chartjs
// see: https://www.chartjs.org/docs/latest/general/data-structures.html
// and see: https://www.chartjs.org/docs/latest/charts/line.html
export const buildProjectionDatasets = (state, props) => {
  const colors = get(props, 'colors')
  const { chartType } = state
  const isScenario = !isEmpty(props.forecasts.scenarioForecast)
  const incomeBreakdownView = state.showTotalExpense || state.showGrossIncome

  // These 2 functions are the actual expensive calls that can be optimized
  const actualDataPoints = constructDataPointsForForecast(props.forecasts.actualForecast, isScenario ? [] : props.cashflowEvents, props)
  const scenarioDataPoints = constructDataPointsForForecast(props.forecasts.scenarioForecast || [], props.cashflowEvents, props)

  const enabledStates = {
    [CHART_NAMES.SCENARIO_FORECAST]: !isEmpty(scenarioDataPoints),
    [CHART_NAMES.ACTUAL_FORECAST]: !(incomeBreakdownView && isScenario),
    [CHART_NAMES.EXPENSE_FORECAST]: state.chartType === ChartTypes.income && state.showTotalExpense,
    [CHART_NAMES.INCOME_FORECAST]: state.chartType === ChartTypes.income && state.showGrossIncome
  }
  const config = buildDatasetConfig(colors, enabledStates, {
    isScenario,
    chartType
  })

  const datasets = createDatasetsForForecasts(actualDataPoints, scenarioDataPoints, config, {
    isScenario,
    chartType,
    retirementAge: props.retirementAge,
    expectedAge: props.expectedAge,
    initialRetirementAge: state.retirementAge,
    showTicksInYears: props.showTicksInYears,
    timeValue: props.timeValue
  })

  return datasets
}

const createDatasetsForForecasts = (actualDataPoints, scenarioDataPoints, config, {
  retirementAge,
  expectedAge,
  initialRetirementAge,
  showTicksInYears,
  timeValue,
  chartType,
  isScenario
}) => {
  const datasets = {
    [CHART_NAMES.SCENARIO_FORECAST]: {
      data: scenarioDataPoints,
      tickIndexes: [
        0,
        findIndex(scenarioDataPoints, { assets: 0 }),
        findIndex(scenarioDataPoints, { age: `${retirementAge}` }),
        findIndex(scenarioDataPoints, { age: `${expectedAge}` }),
        size(scenarioDataPoints) - 1
      ],
      defaultIndex: findIndex(scenarioDataPoints, { age: `${retirementAge}` }),
      tickType: showTicksInYears ? 'year' : 'age',
      parsing: {
        xAxisKey: 'age',
        yAxisKey: `${chartType}.${timeValue}`
      }
    },
    [CHART_NAMES.ACTUAL_FORECAST]: {
      data: actualDataPoints,
      tickIndexes: [
        0,
        findIndex(actualDataPoints, { assets: 0 }),
        findIndex(actualDataPoints, { age: `${initialRetirementAge}` }),
        findIndex(actualDataPoints, { age: `${expectedAge}` }),
        size(actualDataPoints) - 1],
      defaultIndex: findIndex(actualDataPoints, { age: `${retirementAge}` }),
      tickType: showTicksInYears ? 'year' : 'age',
      parsing: {
        xAxisKey: 'age',
        yAxisKey: `${chartType}.${timeValue}`
      }
    },
    [CHART_NAMES.INCOME_FORECAST]: {
      data: isScenario ? scenarioDataPoints : actualDataPoints,
      tickIndexes: [
        0,
        findIndex(actualDataPoints, { assets: 0 }),
        findIndex(actualDataPoints, { age: `${retirementAge}` }),
        findIndex(actualDataPoints, { age: `${expectedAge}` }),
        size(actualDataPoints) - 1],
      defaultIndex: findIndex(actualDataPoints, { age: `${retirementAge}` }),
      tickType: showTicksInYears ? 'year' : 'age',
      subtextKey: `monthlytax.${timeValue}`,
      parsing: {
        xAxisKey: 'age',
        yAxisKey: `grossMonthlyIncome.${timeValue}`
      }
    },
    [CHART_NAMES.EXPENSE_FORECAST]: {
      data: isScenario ? scenarioDataPoints : actualDataPoints,
      tickIndexes: [
        0,
        findIndex(actualDataPoints, { assets: 0 }),
        findIndex(actualDataPoints, { age: `${retirementAge}` }),
        findIndex(scenarioDataPoints, { age: `${expectedAge}` }),
        size(actualDataPoints) - 1],
      defaultIndex: findIndex(actualDataPoints, { age: `${retirementAge}` }),
      tickType: showTicksInYears ? 'year' : 'age',
      parsing: {
        xAxisKey: 'age',
        yAxisKey: `totalMonthlyExpenses.${timeValue}`
      }
    },
  }

  return mapDatasetsToChartJsDatasets(datasets, config)
}

const mapDatasetsToChartJsDatasets = (datasets, config) => {
  return Object.entries(datasets).map(([chartName, dataset]) => {
    if (config[chartName].enabled) {
      return {
      ...dataset,
      ...config[chartName]
      }
    } else {
      return false
    }
  }).filter(Boolean)
}

const constructDataPointsForForecast = (projection, cashflowEvents, props) => {
  const currentAge = get(props, 'currentAge')
  // The screen passes the plan's assumptions, not a bare rate, so reading only
  // `inflation` left every present-value series on 0.06 whatever the client
  // chose — the same defect already fixed on the joint chart.
  const inflation = get(props, 'inflation', get(props, 'assumptions.inflation', 0.06))

  return map(projection, (dataPoint) => ({
    assets: get(dataPoint, 'assets'),
    capital: {
      future: get(dataPoint, 'assets'),
      present: annuity(get(dataPoint, 'assets'), currentAge - toFinite(get(dataPoint, 'age')), inflation)
    },
    income: {
      future: get(dataPoint, 'net_monthly_income'),
      present: Math.round(annuity(get(dataPoint, 'net_monthly_income'), currentAge - toFinite(get(dataPoint, 'age')), inflation))
    },
    grossMonthlyIncome: {
      future: get(dataPoint, 'gross_monthly_income'),
      present: Math.round(annuity(get(dataPoint, 'gross_monthly_income'), currentAge - toFinite(get(dataPoint, 'age')), inflation)),
    },
    monthlytax: {
      future: get(dataPoint, 'gross_monthly_income') - get(dataPoint, 'net_monthly_income'),
      present: Math.round(annuity(
        get(dataPoint, 'gross_monthly_income') - get(dataPoint, 'net_monthly_income'), currentAge - toFinite(get(dataPoint, 'age')), inflation
      ))
    },
    totalMonthlyExpenses: {
      future: get(dataPoint, 'total_monthly_expenses'),
      present: Math.round(annuity(get(dataPoint, 'total_monthly_expenses'), currentAge - toFinite(get(dataPoint, 'age')), inflation))
    },
    age: `${get(dataPoint, 'age')}`,
    year: `${getYearFromAge(get(dataPoint, 'age'), currentAge)}`,
    cashflowEvents: buildProjectionCashflowEventsForAge(cashflowEvents, currentAge, get(dataPoint, 'age'), inflation),
    cashflowEventsDrawStatus: get(dataPoint, 'cashflow_events_draw_status')
  }))
}

const buildProjectionCashflowEventsForAge = (cashflowEvents, currentAge, age, inflation) => {
  const cashflowEventsAtAge = filter(cashflowEvents, (cashflowEvent) => {
    const { start_age, end_age, frequency } = cashflowEvent
    return range(start_age, end_age + 1, frequency).includes(age)
  })

  // Add some extra data here to make it easier for chartjs
  // to work off of cashflowEvents
  return map(cashflowEventsAtAge, (cashflowEvent) => ({
    ...cashflowEvent,
    pointAge: age,
    currentAge,
    inflation: inflation
  }))
}
